"use client";

import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faFileAudio,
  faFileLines,
  faMicrophone,
  faUpload,
  faXmark,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "@/components/ui/dialog/Dialog";
import { Alert } from "@/components/ui/alert/Alert";
import { Button } from "@/components/ui/button/Button";
import { Checkbox } from "@/components/ui/checkbox/Checkbox";
import FormInput from "@/components/ui/form-input";
import TextArea from "@/components/ui/form-input/TextArea";
import SelectDropdown, { type SelectOption } from "@/components/ui/select-dropdown";
import { SegmentedTabs } from "@/components/ui/segmented-tabs";
import * as AgenticV2API from "@/services/api/agentic-v2";
import { extractApiErrorMessage } from "@/lib/extractApiErrorMessage";

export type CreateLeadDialogProps = {
  open: boolean;
  onClose: () => void;
  onCreated?: (result: AgenticV2API.BridgeIntakeResponse) => void;
};

type CreateMode = "intake" | "manual";
type IntakeChannel = "email" | "voice" | "transcript";

const LEAD_SOURCE_OPTIONS = [
  "Client Referral",
  "Employer Referral",
  "Existing contact Friend/Family",
  "Introducer",
  "Networking",
  "Advertisement",
  "Affinity Relationship",
  "Event",
  "IFA Acquisition",
  "Marketing Campaign",
  "Website",
  "Purchased Website",
  "Social Media",
  "Marketing and Sales team",
  "Voice recording",
  "Email",
  "Call transcript",
] as const;

const LEAD_TYPE_OPTIONS = [
  "New Private Client",
  "New Corporate Client",
  "New Contact Existing Account",
  "Professional Introducer",
  "Existing Private Client",
  "Existing Corporate Client",
] as const;

const SAMPLE_EMAIL_BODY = `Hi,

I'm looking to review my pension arrangements ahead of retirement. I'm James Walsh, currently at SW1A 1AA. You can reach me on 07700 900123 or james.walsh@example.com. Portfolio is roughly £850k across two SIPPs.

Best regards,
James Walsh`;

const SAMPLE_VOICE_TRANSCRIPT = `Adviser: Hi David, thanks for calling Saltus — I'm Sarah. I understand you and Helen are looking at joint financial advice?
David: Yes, that's right. I'm David Chen and my wife Helen is here too.
Adviser: Could I take your contact details for the lead record?
David: Sure — my email is david.chen@example.com and mobile is 07700900123. We're at 14 Elm Gardens, postcode SW1A 2AA.
Adviser: And what's prompted you to get in touch?
David: We're approaching retirement with around £1,500,000 across pensions and ISAs and we're happy for you to process our details.`;

const SAMPLE_CALL_TRANSCRIPT = `Adviser: Hi Sarah, thanks for coming in today.
Sarah: Thanks for seeing me — I'm Sarah Mitchell at SW1A 1AA. Email sarah.mitchell@example.com, mobile 07700 900456.
Adviser: And you're looking at roughly £650k across the portfolios?
Sarah: Yes, around £650,000. Happy to proceed with the review.`;

const AUDIO_EXTENSIONS = [".mp3", ".wav", ".m4a", ".webm", ".ogg", ".flac", ".mp4"];
const EMAIL_EXTENSIONS = [".eml", ".msg"];

function isAudioFile(file: File): boolean {
  const lower = file.name.toLowerCase();
  return file.type.startsWith("audio/") || AUDIO_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

function isEmailFile(file: File): boolean {
  const lower = file.name.toLowerCase();
  return EMAIL_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

const TITLE_SELECT_OPTIONS: SelectOption<string>[] = [
  { value: "Mr", label: "Mr" },
  { value: "Mrs", label: "Mrs" },
  { value: "Ms", label: "Ms" },
  { value: "Miss", label: "Miss" },
  { value: "Dr", label: "Dr" },
];

const LEAD_SOURCE_SELECT_OPTIONS: SelectOption<string>[] = LEAD_SOURCE_OPTIONS.map((src) => ({
  value: src,
  label: src,
}));

const LEAD_TYPE_SELECT_OPTIONS: SelectOption<string>[] = LEAD_TYPE_OPTIONS.map((t) => ({
  value: t,
  label: t,
}));

const PROPOSITION_SELECT_OPTIONS: SelectOption<string>[] = [
  { value: "SFP", label: "SFP (Saltus Financial Planning)" },
  { value: "SPP", label: "SPP (Saltus Partnership Programme)" },
];

export function CreateLeadDialog({ open, onClose, onCreated }: CreateLeadDialogProps) {
  const [mode, setMode] = useState<CreateMode>("intake");

  // Intake state
  const [channel, setChannel] = useState<IntakeChannel>("email");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fromAddress, setFromAddress] = useState("");
  const [subject, setSubject] = useState("");
  const [intakeText, setIntakeText] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Manual form state
  const [title, setTitle] = useState("Mr");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [mobileTelephone, setMobileTelephone] = useState("");
  const [postcode, setPostcode] = useState("");
  const [adviserId, setAdviserId] = useState("adv-bath-1");
  const [proposition, setProposition] = useState("SFP");
  const [leadType, setLeadType] = useState<string>(LEAD_TYPE_OPTIONS[0]);
  const [leadSource, setLeadSource] = useState<string>("Client Referral");
  const [opportunityValue, setOpportunityValue] = useState("");
  const [secondaryFirstName, setSecondaryFirstName] = useState("");
  const [secondaryLastName, setSecondaryLastName] = useState("");
  const [notes, setNotes] = useState("");
  const [processingConsent, setProcessingConsent] = useState(true);
  const [adviserOptions, setAdviserOptions] = useState<AgenticV2API.OnboardingAdviserOption[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    AgenticV2API.getOnboardingAdvisers()
      .then((res) => {
        if (cancelled) return;
        setAdviserOptions(res.items ?? []);
        if (res.items?.length && !adviserId) {
          setAdviserId(res.items[0].value);
        }
      })
      .catch(() => {
        // Fallback options if endpoint unavailable
      });
    return () => {
      cancelled = true;
    };
  }, [open, adviserId]);

  const resetState = () => {
    setMode("intake");
    setChannel("email");
    setSelectedFile(null);
    setFromAddress("");
    setSubject("");
    setIntakeText("");
    setTitle("Mr");
    setFirstName("");
    setLastName("");
    setEmailAddress("");
    setMobileTelephone("");
    setPostcode("");
    setProposition("SFP");
    setLeadType(LEAD_TYPE_OPTIONS[0]);
    setLeadSource("Client Referral");
    setOpportunityValue("");
    setSecondaryFirstName("");
    setSecondaryLastName("");
    setNotes("");
    setProcessingConsent(true);
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    resetState();
    onClose();
  };

  const handleFilePicked = async (file: File | null) => {
    setError(null);
    if (!file) {
      setSelectedFile(null);
      return;
    }
    const lower = file.name.toLowerCase();
    if (isEmailFile(file)) {
      setChannel("email");
      setSelectedFile(file);
      return;
    }
    if (isAudioFile(file)) {
      setChannel("voice");
      setSelectedFile(file);
      return;
    }
    if (lower.endsWith(".txt") || file.type === "text/plain") {
      try {
        const text = await file.text();
        setIntakeText(text);
        setSelectedFile(file);
        if (channel === "email") {
          setChannel("transcript");
        }
      } catch {
        setError("Couldn't read text file.");
      }
      return;
    }
    setError("Unsupported file type. Upload .mp3/.wav/.m4a audio, .eml/.msg email, or .txt transcript.");
  };

  const handleLoadSample = () => {
    setError(null);
    setSelectedFile(null);
    if (channel === "email") {
      setFromAddress("james.walsh@example.com");
      setSubject("Pension review enquiry");
      setIntakeText(SAMPLE_EMAIL_BODY);
    } else if (channel === "voice") {
      setIntakeText(SAMPLE_VOICE_TRANSCRIPT);
    } else {
      setIntakeText(SAMPLE_CALL_TRANSCRIPT);
    }
  };

  const canSubmitIntake =
    !submitting &&
    (Boolean(selectedFile && (isEmailFile(selectedFile) || isAudioFile(selectedFile))) ||
      intakeText.trim().length > 0);

  const canSubmitManual =
    !submitting &&
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    (emailAddress.trim().length > 0 || mobileTelephone.trim().length > 0);

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      let result: AgenticV2API.BridgeIntakeResponse;
      if (mode === "intake") {
        if (selectedFile && isEmailFile(selectedFile)) {
          result = await AgenticV2API.bridgeOnboardingIntakeEml(selectedFile);
        } else if (selectedFile && isAudioFile(selectedFile)) {
          result = await AgenticV2API.bridgeOnboardingIntakeAudio(selectedFile);
        } else {
          result = await AgenticV2API.bridgeOnboardingIntake({
            channel,
            text: intakeText.trim(),
            source: selectedFile ? "upload" : "paste",
            subject: channel === "email" && subject.trim() ? subject.trim() : undefined,
            from_address: channel === "email" && fromAddress.trim() ? fromAddress.trim() : undefined,
          });
        }
      } else {
        result = await AgenticV2API.createManualOnboardingLead({
          title: title.trim() || undefined,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email_address: emailAddress.trim() || undefined,
          mobile_telephone: mobileTelephone.trim() || undefined,
          postcode: postcode.trim() || undefined,
          adviser_id: adviserId.trim() || undefined,
          proposition: proposition.trim() || "SFP",
          lead_type: leadType,
          lead_source: leadSource,
          opportunity_value: opportunityValue.trim() ? opportunityValue.trim() : undefined,
          processing_consent: processingConsent,
          secondary_contact_first_name: secondaryFirstName.trim() || undefined,
          secondary_contact_last_name: secondaryLastName.trim() || undefined,
          notes: notes.trim() || undefined,
        });
      }
      resetState();
      onClose();
      onCreated?.(result);
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err, "Couldn't create lead"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} size="2xl" data-testid="create-lead-dialog">
      <DialogTitle>Create lead</DialogTitle>
      <DialogBody>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Import an email (<code>.eml</code> / <code>.msg</code>), voice recording (<code>.mp3</code>), or call transcript for Ani to extract, or enter the lead details manually.
        </p>

        <div className="mt-4">
          <SegmentedTabs
            tabs={[
              { id: "intake", label: "Paste or upload" },
              { id: "manual", label: "Manual form" },
            ]}
            activeId={mode}
            onChange={(id) => {
              setError(null);
              setMode(id as CreateMode);
            }}
          />
        </div>

        {mode === "intake" ? (
          <div className="mt-4 space-y-4" data-testid="create-lead-intake-panel">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Intake channel">
                {(
                  [
                    { id: "email", label: "Email (.eml / .msg)", icon: faEnvelope },
                    { id: "voice", label: "Voice recording (.mp3)", icon: faMicrophone },
                    { id: "transcript", label: "Call transcript", icon: faFileLines },
                  ] as const
                ).map((item) => {
                  const active = channel === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      data-testid={`create-lead-channel-${item.id}`}
                      onClick={() => {
                        setChannel(item.id);
                        setError(null);
                      }}
                      className={clsx(
                        "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors",
                        active
                          ? "bg-indigo-600 text-white shadow-xs"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700",
                      )}
                    >
                      <FontAwesomeIcon icon={item.icon} className="h-3.5 w-3.5" />
                      {item.label}
                    </button>
                  );
                })}
              </div>

              <Button
                type="button"
                plain
                size="xs"
                onClick={handleLoadSample}
                data-testid="create-lead-load-sample"
              >
                Load sample
              </Button>
            </div>

            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files?.[0] ?? null;
                void handleFilePicked(file);
              }}
              className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-slate-300 bg-slate-50/70 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/50"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".mp3,.wav,.m4a,.webm,.ogg,.flac,.eml,.msg,.txt,audio/*,message/rfc822,text/plain"
                className="hidden"
                data-testid="create-lead-file-input"
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  void handleFilePicked(file);
                }}
              />
              {selectedFile ? (
                <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <FontAwesomeIcon
                      icon={isAudioFile(selectedFile) ? faFileAudio : faEnvelope}
                      className="h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-400"
                    />
                    <div className="min-w-0">
                      <p
                        className="truncate text-sm font-medium text-slate-900 dark:text-slate-100"
                        data-testid="create-lead-selected-file"
                      >
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {(selectedFile.size / 1024).toFixed(1)} KB · Ready to import
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    className="rounded-md p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                    aria-label="Remove file"
                    data-testid="create-lead-clear-file"
                  >
                    <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex flex-1 flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5 text-xs text-slate-600 dark:text-slate-300">
                    <FontAwesomeIcon icon={faUpload} className="h-4 w-4 text-slate-400" />
                    <span>
                      Drop an <strong>.mp3</strong> audio recording, <strong>.eml</strong> / <strong>.msg</strong> email, or <strong>.txt</strong> file here
                    </span>
                  </div>
                  <Button
                    type="button"
                    outline
                    size="xs"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="create-lead-browse-button"
                  >
                    Browse file
                  </Button>
                </div>
              )}
            </div>

            {channel === "email" && (!selectedFile || !isEmailFile(selectedFile)) ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormInput
                  id="create-lead-email-from"
                  data-testid="create-lead-email-from"
                  label="From address"
                  optional
                  value={fromAddress}
                  onChange={(e) => setFromAddress(e.target.value)}
                  placeholder="client@example.com"
                />
                <FormInput
                  id="create-lead-email-subject"
                  data-testid="create-lead-email-subject"
                  label="Subject"
                  optional
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Pension review enquiry"
                />
              </div>
            ) : null}

            {!selectedFile || (!isEmailFile(selectedFile) && !isAudioFile(selectedFile)) ? (
              <TextArea
                id="create-lead-intake-text"
                data-testid="create-lead-intake-text"
                label={
                  channel === "email"
                    ? "Paste email body"
                    : channel === "voice"
                      ? "Paste voice recording transcript (or upload .mp3 above)"
                      : "Paste call transcript or adviser notes"
                }
                value={intakeText}
                onChange={(e) => setIntakeText(e.target.value)}
                rows={7}
                placeholder={
                  channel === "email"
                    ? "Paste the enquiry email content here…"
                    : channel === "voice"
                      ? "Paste voice transcript or upload an .mp3 file above…"
                      : "Paste call transcript or meeting notes here…"
                }
              />
            ) : null}
          </div>
        ) : (
          <div className="mt-4 space-y-4" data-testid="create-lead-manual-panel">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
              <div className="sm:col-span-2">
                <SelectDropdown
                  label="Title"
                  options={TITLE_SELECT_OPTIONS}
                  value={title}
                  onChange={setTitle}
                  buttonContainerClassName="mt-2"
                  data-testid="manual-lead-title"
                />
              </div>
              <div className="sm:col-span-5">
                <FormInput
                  id="manual-lead-first-name"
                  data-testid="manual-lead-first-name"
                  label="First name *"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="James"
                />
              </div>
              <div className="sm:col-span-5">
                <FormInput
                  id="manual-lead-last-name"
                  data-testid="manual-lead-last-name"
                  label="Last name *"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Thompson"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <FormInput
                id="manual-lead-email"
                data-testid="manual-lead-email"
                label="Email address *"
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                placeholder="james.thompson@example.com"
              />
              <FormInput
                id="manual-lead-mobile"
                data-testid="manual-lead-mobile"
                label="Mobile telephone"
                type="tel"
                value={mobileTelephone}
                onChange={(e) => setMobileTelephone(e.target.value)}
                placeholder="07700 900123"
              />
              <FormInput
                id="manual-lead-postcode"
                data-testid="manual-lead-postcode"
                label="Postcode"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                placeholder="SW1A 1AA"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <SelectDropdown
                label="Adviser"
                options={
                  adviserOptions.length > 0
                    ? adviserOptions
                    : [{ value: "adv-bath-1", label: "Bath Adviser (Default)" }]
                }
                value={adviserId}
                onChange={setAdviserId}
                buttonContainerClassName="mt-2"
                data-testid="manual-lead-adviser"
              />
              <SelectDropdown
                label="Lead source"
                options={LEAD_SOURCE_SELECT_OPTIONS}
                value={leadSource}
                onChange={setLeadSource}
                buttonContainerClassName="mt-2"
                data-testid="manual-lead-source"
              />
              <FormInput
                id="manual-lead-value"
                data-testid="manual-lead-value"
                label="Opportunity value (£)"
                type="number"
                value={opportunityValue}
                onChange={(e) => setOpportunityValue(e.target.value)}
                placeholder="750000"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <SelectDropdown
                label="Proposition"
                options={PROPOSITION_SELECT_OPTIONS}
                value={proposition}
                onChange={setProposition}
                buttonContainerClassName="mt-2"
                data-testid="manual-lead-proposition"
              />
              <SelectDropdown
                label="Lead type"
                options={LEAD_TYPE_SELECT_OPTIONS}
                value={leadType}
                onChange={setLeadType}
                buttonContainerClassName="mt-2"
                data-testid="manual-lead-type"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormInput
                id="manual-lead-sec-first"
                data-testid="manual-lead-sec-first"
                label="Secondary contact first name"
                optional
                value={secondaryFirstName}
                onChange={(e) => setSecondaryFirstName(e.target.value)}
                placeholder="Helen"
              />
              <FormInput
                id="manual-lead-sec-last"
                data-testid="manual-lead-sec-last"
                label="Secondary contact last name"
                optional
                value={secondaryLastName}
                onChange={(e) => setSecondaryLastName(e.target.value)}
                placeholder="Thompson"
              />
            </div>

            <TextArea
              id="manual-lead-notes"
              data-testid="manual-lead-notes"
              label="Enquiry notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Background notes, retirement goals, or referral context…"
            />

            <div className="pt-1">
              <Checkbox
                checked={processingConsent}
                onChange={setProcessingConsent}
                label="Processing consent (GDPR) granted by the client"
                data-testid="manual-lead-consent"
              />
            </div>
          </div>
        )}

        {error ? (
          <Alert variant="error" borderless className="mt-4" data-testid="create-lead-error">
            {error}
          </Alert>
        ) : null}
      </DialogBody>
      <DialogActions>
        <Button
          type="button"
          color="indigo"
          loading={submitting}
          disabled={mode === "intake" ? !canSubmitIntake : !canSubmitManual}
          onClick={() => void handleSubmit()}
          data-testid="create-lead-submit"
        >
          {mode === "intake" ? "Import & create lead" : "Create lead"}
        </Button>
        <Button
          type="button"
          plain
          onClick={handleClose}
          disabled={submitting}
          data-testid="create-lead-cancel"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
